
import { WeatherData, AirQualityData, MarineData } from '../types';

const WEATHER_CACHE_DURATION = 1 * 60 * 1000;
const AQI_CACHE_DURATION = 5 * 60 * 1000;
const MARINE_CACHE_DURATION = 5 * 60 * 1000;
// const BMKG_API_URL = 'https://staklim-jateng.bmkg.go.id/api_weather';

// In-memory cache to replace localStorage for better realtime behavior
const memoryCache = new Map<string, { data: any, timestamp: number }>();

// Helpers
const getCachedData = (key: string, duration: number) => {
  const cached = memoryCache.get(key);
  if (!cached) return null;
  
  const isStale = Date.now() - cached.timestamp > duration;
  if (isStale) return { ...cached.data, isStale: true };
  return { ...cached.data, updatedAt: cached.timestamp };
};

const setCachedData = (key: string, data: any) => {
  memoryCache.set(key, { data, timestamp: Date.now() });
};

// Internal Helper for Fetching JSON from Open-Meteo via Proxy
const fetchOpenMeteoJson = async (url: string, params: any) => {
  const queryString = new URLSearchParams();
  Object.keys(params).forEach(key => {
    if (Array.isArray(params[key])) {
      queryString.append(key, params[key].join(','));
    } else {
      queryString.append(key, String(params[key]));
    }
  });
  
  // Ensure we request JSON by NOT requesting FlatBuffers (default is JSON)
  // Our proxy forwards everything. 
  
  const fullUrl = `${url}?${queryString.toString()}`;
  const response = await fetch(fullUrl);
  
  if (!response.ok) {
    throw new Error(`OpenMeteo API Error: ${response.status} ${response.statusText}`);
  }
  
  return await response.json();
};

/*
const getCurrentPeriod = (): keyof BMKGCity['periods'] => {
  const hour = new Date().getHours();
  if (hour >= 0 && hour < 6) return 'dini_hari';
  if (hour >= 6 && hour < 11) return 'pagi';
  if (hour >= 11 && hour < 15) return 'siang';
  if (hour >= 15 && hour < 24) return 'malam';
  return 'siang';
};

const mapBMKGIcon = (iconName: string): string => {
  if (iconName.includes('sun')) return '☀️';
  if (iconName.includes('drizzle')) return '🌧️';
  if (iconName.includes('rain')) return '🌧️';
  if (iconName.includes('lightning')) return '⛈️';
  if (iconName.includes('cloud')) return '☁️';
  return '☁️';
};
*/

// Implementations
// Shared Helper to Parse OpenMeteo Response
const parseOpenMeteoResponse = (response: any, locationName: string): WeatherData | null => {
    if (!response || !response.hourly) return null;

    const hourly = response.hourly;
    const now = Math.floor(Date.now() / 1000);
    
    // Find current index
    let currentIndex = 0;
    let minDiff = Infinity;
    
    for (let i = 0; i < hourly.time.length; i++) {
        const diff = Math.abs(now - hourly.time[i]);
        if (diff < minDiff) {
            minDiff = diff;
            currentIndex = i;
        }
    }
    
    // Extract variables
    const temperature_2m = hourly.temperature_2m[currentIndex];
    const wind_speed_10m = hourly.wind_speed_10m[currentIndex];
    const weather_code = hourly.weather_code[currentIndex];
    const relative_humidity_2m = hourly.relative_humidity_2m[currentIndex];

    // Map WMO Weather Codes
    let condition = "Berawan";
    let icon = "☁️";
    let rainChance = "10%";
    const code = weather_code;

    if (code === 0) { condition = "Cerah"; icon = "☀️"; rainChance = "0%"; }
    else if (code <= 3) { condition = "Berawan"; icon = "⛅"; rainChance = "10%"; }
    else if (code <= 48) { condition = "Berkabut"; icon = "🌫️"; rainChance = "20%"; }
    else if (code <= 55) { condition = "Gerimis"; icon = "🌧️"; rainChance = "60%"; }
    else if (code <= 65) { condition = "Hujan"; icon = "🌧️"; rainChance = "80%"; }
    else if (code <= 82) { condition = "Hujan Deras"; icon = "⛈️"; rainChance = "90%"; }
    else if (code >= 95) { condition = "Badai Petir"; icon = "⛈️"; rainChance = "95%"; }

    return {
      temp: `${Math.round(temperature_2m)}°C`,
      condition: condition,
      humidity: `${Math.round(relative_humidity_2m)}%`,
      rainChance: rainChance,
      icon: icon,
      location: locationName,
      windSpeed: `${Math.round(wind_speed_10m)} km/j`,
      source: 'OpenMeteo'
    };
};

export async function fetchWeatherByCoordinates(lat: number, lon: number, locationName: string = 'Lokasi Anda'): Promise<WeatherData | null> {
  try {
    const params = {
      latitude: lat,
      longitude: lon,
      hourly: ["temperature_2m", "rain", "showers", "snowfall", "pressure_msl", "cloud_cover", "cloud_cover_low", "cloud_cover_high", "cloud_cover_mid", "wind_direction_180m", "wind_speed_10m", "wind_speed_80m", "wind_speed_180m", "wind_direction_10m", "wind_speed_120m", "wind_direction_80m", "wind_direction_120m", "soil_temperature_0cm", "soil_temperature_6cm", "soil_temperature_18cm", "soil_temperature_54cm", "soil_moisture_0_to_1cm", "temperature_80m", "temperature_180m", "temperature_120m", "weather_code", "precipitation", "relative_humidity_2m", "visibility"],
      timezone: "Asia/Bangkok",
      past_days: 1,
      type: 'forecast',
      timeformat: 'unixtime'
    };
    
    const url = "/api/weather";
    const response = await fetchOpenMeteoJson(url, params);
    return parseOpenMeteoResponse(response, locationName);
  } catch (e) {
    console.error("OpenMeteo Coordinate Fetch Error:", e);
    return null;
  }
}

export async function fetchOpenMeteoWeather(location: string): Promise<WeatherData | null> {
  try {
    // Default to Tegal Kota
    let lat = -6.8694;
    let lon = 109.1402;
    let locName = 'Kota Tegal';

    const normalizedLoc = location.toLowerCase();
    if (normalizedLoc.includes('kab') || normalizedLoc.includes('slawi') || normalizedLoc.includes('adiwerna')) {
      lat = -6.9843; // Slawi
      lon = 109.1375;
      locName = 'Kab. Tegal';
    } else if (normalizedLoc.includes('bumijawa') || normalizedLoc.includes('guci') || normalizedLoc.includes('bojong')) {
      lat = -7.168; // Bumijawa area
      lon = 109.167;
      locName = 'Bumijawa/Guci';
    }

    const params = {
      latitude: lat,
      longitude: lon,
      hourly: ["temperature_2m", "rain", "showers", "snowfall", "pressure_msl", "cloud_cover", "cloud_cover_low", "cloud_cover_high", "cloud_cover_mid", "wind_direction_180m", "wind_speed_10m", "wind_speed_80m", "wind_speed_180m", "wind_direction_10m", "wind_speed_120m", "wind_direction_80m", "wind_direction_120m", "soil_temperature_0cm", "soil_temperature_6cm", "soil_temperature_18cm", "soil_temperature_54cm", "soil_moisture_0_to_1cm", "temperature_80m", "temperature_180m", "temperature_120m", "weather_code", "precipitation", "relative_humidity_2m", "visibility"],
      timezone: "Asia/Bangkok",
      past_days: 1,
      type: 'forecast',
      timeformat: 'unixtime' // Request unixtime for easier parsing
    };
    
    // Use server-side proxy
    const url = "/api/weather";
    const response = await fetchOpenMeteoJson(url, params);
    
    return parseOpenMeteoResponse(response, locName);
  } catch (e) {
    console.error("OpenMeteo Fetch Error:", e);
    return null;
  }
}

/*
export async function fetchRealBMKGWeather(location: string): Promise<WeatherData | null> {
  try {
    const response = await fetchWithTimeout(BMKG_API_URL, {}, 8000); // 8s timeout for BMKG
    if (!response.ok) return null;
    const json: BMKGResponse = await response.json();
    if (!json.success || !json.data) return null;

    // Comprehensive Location Mapping
    const normalizedLoc = location.toLowerCase();
    
    // Kota Tegal Districts
    const kotaDistricts = ['tegal barat', 'tegal timur', 'tegal selatan', 'margadana', 'kota tegal'];
    
    // Kab Tegal Districts (Major ones)
    const kabDistricts = [
      'slawi', 'adiwerna', 'dukuhturi', 'talang', 'tarub', 'kramat', 'suradadi', 'warureja', 
      'kedungbanteng', 'pangkah', 'jatinegara', 'lebaksiu', 'balapulang', 'pagerbarang', 
      'margasari', 'bumijawa', 'bojong', 'dukuhwaru', 'guci', 'kab. tegal', 'kabupaten tegal'
    ];

    let targetCity = '';
    
    if (kotaDistricts.some(d => normalizedLoc.includes(d))) {
      targetCity = 'Kota Tegal';
    } else if (kabDistricts.some(d => normalizedLoc.includes(d))) {
      targetCity = 'Kab. Tegal';
    } else {
      // Default fallback based on string matching if not in lists
      targetCity = normalizedLoc.includes('kab') ? 'Kab. Tegal' : 'Kota Tegal';
    }
    
    const cityData = json.data.find(d => d.city === targetCity);

    if (!cityData) return null;

    const period = getCurrentPeriod();
    const weather = cityData.periods[period];

    return {
      temp: `${weather.temp}°C`,
      condition: weather.weather,
      humidity: `${weather.humidity}%`,
      rainChance: weather.weather.toLowerCase().includes('hujan') ? '70%' : '10%',
      icon: mapBMKGIcon(weather.icon),
      location: cityData.city,
      windSpeed: `${weather.wind_speed} km/j`,
      source: 'BMKG'
    };
  } catch (e) {
    console.error("BMKG Fetch Error:", e);
    return null;
  }
}
*/

export const fetchTegalWeather = async (location: string = 'Tegal', forceRefresh: boolean = false): Promise<WeatherData> => {
  const cacheKey = `weather_v3_${location.replace(/\s/g, '_')}`;
  const cached = getCachedData(cacheKey, WEATHER_CACHE_DURATION);
  if (!forceRefresh && cached && !cached.isStale) return cached;

  // Try OpenMeteo First (Primary Provider)
  try {
    const omData = await fetchOpenMeteoWeather(location);
    if (omData) {
      setCachedData(cacheKey, omData);
      return omData;
    }
  } catch (e) {
    console.warn("OpenMeteo failed...", e);
  }

  // Fallback: Try BMKG Real Data (Disabled as per request)
  /*
  const bmkgData = await fetchRealBMKGWeather(location);
  if (bmkgData) {
    setCachedData(cacheKey, bmkgData);
    return bmkgData;
  }
  */

  // If both fail, return a basic fallback object (without AI)
  // The UI will handle this or we can return a "service unavailable" state
  // But for now, return cached stale data or empty placeholder
  if (cached) return { ...cached, isStale: true };

  return {
    temp: "--°C",
    condition: "Tidak Tersedia",
    humidity: "--%",
    rainChance: "--%",
    icon: "☁️",
    location: location,
    windSpeed: "-- km/j",
    source: 'N/A' as any
  };
};

export async function fetchAirQuality(location: string = 'Kota Tegal', forceRefresh: boolean = false): Promise<AirQualityData | null> {
  const cacheKey = `aqi_${location.replace(/\s/g, '_')}`;
  const cached = getCachedData(cacheKey, AQI_CACHE_DURATION);
  if (!forceRefresh && cached && !cached.isStale) return cached;

  try {
    let lat = -6.8694;
    let lon = 109.1402;
    if (location.toLowerCase().includes('slawi') || location.toLowerCase().includes('kab')) {
      lat = -6.9843;
      lon = 109.1375;
    }

    const params = {
      latitude: lat,
      longitude: lon,
      current: ["us_aqi", "pm10", "pm2_5"],
      timezone: "Asia/Bangkok",
      type: 'air-quality'
    };
    const url = "/api/weather";
    const response = await fetchOpenMeteoJson(url, params);
    
    const current = response.current;
    if (!current) return null;
    
    const aqi = Math.round(current.us_aqi);
    const pm10 = Math.round(current.pm10);
    const pm25 = Math.round(current.pm2_5);

    let status: AirQualityData['status'] = 'Baik';
    if (aqi > 300) status = 'Berbahaya';
    else if (aqi > 150) status = 'Tidak Sehat';
    else if (aqi > 100) status = 'Sedang';
    
    const result: AirQualityData = {
      aqi,
      pm10,
      pm25,
      status,
      updatedAt: Date.now()
    };

    setCachedData(cacheKey, result);
    return result;
  } catch (e) {
    console.error("AQI Fetch Error:", e);
    return null;
  }
}

export async function fetchMarineForecast(forceRefresh: boolean = false): Promise<MarineData | null> {
  const cacheKey = 'marine_tegal_v1';
  const cached = getCachedData(cacheKey, MARINE_CACHE_DURATION);
  if (!forceRefresh && cached && !cached.isStale) return cached;

  try {
    // Tegal Coastal Point (Pantai Alam Indah approx)
    const lat = -6.85; 
    const lon = 109.14;
    
    const params = {
      latitude: lat,
      longitude: lon,
      hourly: ["wave_height", "wave_direction", "wave_period"],
      timezone: "Asia/Bangkok",
      type: 'marine',
      timeformat: 'unixtime'
    };

    const url = "/api/weather";
    const response = await fetchOpenMeteoJson(url, params);
    
    // Process hourly data to get current condition
    const hourly = response.hourly;
    if (!hourly) return null;

    const now = Math.floor(Date.now() / 1000);
    // hourly.time is array of numbers (unixtime)
    let currentIndex = 0;
    let minDiff = Infinity;
    
    for (let i = 0; i < hourly.time.length; i++) {
        const diff = Math.abs(now - hourly.time[i]);
        if (diff < minDiff) {
            minDiff = diff;
            currentIndex = i;
        }
    }

    const waveHeight = Number(hourly.wave_height[currentIndex].toFixed(2));
    const waveDirection = Math.round(hourly.wave_direction[currentIndex]);
    
    // Rob Potential Logic
    // > 1.2m considered high for north java coast usually
    // > 0.5m is normal to moderate
    const isRobPotential = waveHeight > 1.2;

    const result: MarineData = {
      waveHeight,
      waveDirection,
      isRobPotential,
      updatedAt: Date.now()
    };

    setCachedData(cacheKey, result);
    return result;
  } catch (e) {
    console.error("Marine Fetch Error:", e);
    return null;
  }
}

// Analysis Helper: "Ramal dari API"
// Takes weather data and returns a status string without hitting AI
export const analyzeWeatherStatus = (weather: WeatherData): string => {
  if (!weather || (weather.source as any) === 'N/A') return "Data cuaca tidak tersedia.";

  const cond = weather.condition.toLowerCase();
  const rainChance = parseInt(weather.rainChance.replace('%', '')) || 0;
  const wind = parseInt(weather.windSpeed.replace(' km/j', '')) || 0;

  if (cond.includes('badai') || wind > 40 || rainChance > 80) {
    return "Status: WASPADA. Cuaca ekstrem terdeteksi. Harap berhati-hati.";
  }
  
  if (cond.includes('hujan') || rainChance > 50) {
    return "Status: SIAGA HUJAN. Sedia payung/jas hujan jika beraktivitas.";
  }

  if (cond.includes('cerah') || cond.includes('berawan')) {
    return "Status: KONDUSIF. Cuaca mendukung aktivitas luar ruangan.";
  }

  return `Kondisi ${weather.condition} dengan suhu ${weather.temp}.`;
};

export interface RainfallWarning {
  status: 'Aman' | 'Waspada' | 'Siaga' | 'Awas';
  message: string;
  regions: string[];
  updatedAt: number;
}

export const analyzeRainfallWarning = async (forceRefresh: boolean = false): Promise<RainfallWarning> => {
  try {
    const [kota, kab] = await Promise.all([
      fetchTegalWeather('Kota Tegal', forceRefresh),
      fetchTegalWeather('Kab. Tegal', forceRefresh)
    ]);
    
    // Analyze based on OpenMeteo data
    const analyze = (w: WeatherData) => {
      const rain = parseInt(w.rainChance.replace('%', '')) || 0;
      const wind = parseInt(w.windSpeed?.replace(' km/j', '') || '0') || 0;
      const cond = w.condition.toLowerCase();
      
      // Siaga: Badai, Angin Kencang > 45km/j, atau Hujan Deras (>80%)
      if (cond.includes('badai') || wind > 45 || rain > 80) return 3; 

      // Waspada: Hujan/Gerimis dengan intensitas > 50% atau angin > 25km/j
      if ((cond.includes('hujan') || cond.includes('gerimis')) && (rain >= 50 || wind > 25)) return 2; 

      // Waspada Ringan (Aman): Hujan ringan atau mendung tebal
      if (cond.includes('hujan') || cond.includes('gerimis') || rain > 30) return 1; 
      
      return 0; // Aman
    };

    const scoreKota = analyze(kota);
    const scoreKab = analyze(kab);
    const maxScore = Math.max(scoreKota, scoreKab);

    let status: RainfallWarning['status'] = 'Aman';
    let message = 'Cuaca terpantau kondusif di wilayah Tegal Raya.';
    let regions: string[] = [];

    if (maxScore >= 3) {
      status = 'Siaga';
      message = 'POTENSI CUACA EKSTREM! Hujan lebat disertai angin kencang.';
      if (scoreKota >= 3) regions.push('Kota Tegal');
      if (scoreKab >= 3) regions.push('Kab. Tegal');
    } else if (maxScore >= 2) {
      status = 'Waspada';
      message = 'Waspada potensi hujan sedang-lebat. Sedia payung.';
      if (scoreKota >= 2) regions.push('Kota Tegal');
      if (scoreKab >= 2) regions.push('Kab. Tegal');
    } else if (maxScore === 1) {
      // Still Aman but rainy
      status = 'Aman'; 
      message = 'Hujan ringan terdeteksi, tetap berhati-hati saat berkendara.';
    }

    return {
      status,
      message,
      regions,
      updatedAt: Date.now()
    };
  } catch (e) {
    return {
      status: 'Aman',
      message: 'Data cuaca tidak tersedia sementara.',
      regions: [],
      updatedAt: Date.now()
    };
  }
};
